import Swal from "sweetalert2";
import { addEvento, updateEvento } from "../services/cols/eventos.service";
import { createAsignacion, updateAsignacion } from "../services/cols/asignaciones.service";
import { getSectores } from "../services/cols/sectores.service";
import { supabase } from "../supabase/SupabaseClient";
import { fechaParaGuardar } from "../shared/fechaAr";
import { RANGO_SUPABASE } from "./serieEventos";

export const validarFormulario = (data, campos) => {
  const faltanCampos = campos.some(
    (cp) =>
      cp.important &&
      (data[cp.key] === null ||
        data[cp.key] === undefined ||
        data[cp.key].toString().trim() === "")
  );

  if (faltanCampos) {
    Swal.fire({
      title: "Faltan datos",
      text: "Complete los campos obligatorios.",
      icon: "question",
      confirmButtonText: "Entendido",
      confirmButtonColor: "#4161bd",
    });
    return false;
  }

  return true;
};

export const mapearDatosFormulario = (data, campos) => {
  return campos.reduce((acc, cp) => {
    if (cp.use !== "database") return acc;

    let valor = data[cp.key];

    if (valor === "") valor = null;

    if (cp.type === "group") {
      acc[cp.key] = data[cp.key] || {};
      return acc;
    }

    switch (cp.dato) {
      case "number":
        valor = valor === null ? null : Number(valor);
        break;

      case "text":
        valor = valor === null ? null : String(valor).toUpperCase();
        break;

      case "date":
        // El día escrito viaja como texto; new Date() lo corría un día (fechaAr.js)
        valor = valor ? fechaParaGuardar(valor) : null;
        break;

      case "boolean":
        valor = Boolean(valor);
        break;

      case "array":
        valor = Array.isArray(valor) ? valor : [];
        break;

      default:
        valor = valor === "" ? null : valor;
    }

    acc[cp.key] = valor;

    return acc;
  }, {});
};

/* ── EL NÚMERO DE LOS EVENTOS QUE NACEN ACÁ ──────────────────────────────
 *
 * Desde 50.000.000, con el mismo prefijo y ocho dígitos: `005-50000192`.
 *
 * `public.eventos` tiene dos escritores que numeran la misma serie sin verse.
 * CantApp (el Firestore bess-b914c) sigue cargando con su propio correlativo,
 * y `sync-cantapp.yml` baja esas filas cada 3 h PISANDO POR ID. El 21/10 los
 * contadores de `sectores` estaban todos atrás de lo que ya había bajado (el
 * 005 en 191 con el 192..236 ocupados; portería en 16.979 con filas hasta el
 * 25.361): cada carga chocaba (409, 23505). Entre el 14/10 y el 20/10 fallaron
 * así 106 cargas de portería. Adelantar el contador al máximo + 1 sólo cambia
 * el choque de lugar: el número libre de hoy es el que CantApp usa mañana.
 *
 * El número sale del contador de siempre (atómico) y, si viene por debajo, se
 * le suma 50.000.000: la misma cuenta que el SQL propuesto al dueño
 * (`ultimo + 50000000`), así que la serie es una sola con ese SQL o sin él.
 * El bot hace lo mismo (`eventos_numero.py`, en fleet-bot-pastor), y el sync
 * no pisa ni borra una fila de este tramo (`TRAMO_PROPIO`, scripts/sync-cantapp).
 */
export { RANGO_SUPABASE };
const TOPE = 100000000; // ocho dígitos
const INTENTOS = 5;

export const enRango = (k) => {
  const n = Number(k);
  return n >= RANGO_SUPABASE ? n : n + RANGO_SUPABASE;
};

export const idEvento = (sector, n) =>
  `${String(sector).padStart(3, "0")}-${String(n).padStart(8, "0")}`;

const esNumeroRepetido = (error) => error?.code === "23505";

/* Guarda el evento y devuelve `{ id }`. Si el número que tocó ya estaba
 * usado (23505), pide otro, hasta INTENTOS veces; cualquier otro error sube
 * tal cual. Con `{ avisar: false }` no muestra el cartel de «Carga correcta»:
 * lo usa el que todavía tiene pasos por hacer después del evento
 * (`moverConEvento`). */
export const guardarEvento = async (
  elemento,
  data,
  formData,
  campos,
  coleccion,
  modoEdicion = false,
  asignacion = false,
  user = null,
  { avisar = true } = {}
) => {

  if (modoEdicion) {

    if (asignacion) {
      await updateAsignacion(
        elemento.id, {
        ...data,
        ultimaModificacion: new Date().toISOString(),
      }
      )
    } else {
      await updateEvento(elemento.id, {
        ...data,
        ultimaModificacion: new Date().toISOString(),
      });
    }

    Swal.fire(
      "Actualizado",
      `Elemento ${elemento.id} actualizado correctamente.`,
      "success"
    );

    return;
  }

  // ---------------------------------------------------
  // CREACIÓN
  // ---------------------------------------------------

  const sectores = await getSectores();

  const campoId = campos.find((cp) => cp.isId);

  // El número se pide de nuevo en cada intento: si el que tocó estaba usado,
  // el siguiente del contador es otro. Con el id escrito en el formulario no
  // hay a quién pedirle otro, así que se intenta una sola vez.
  const intentos = campoId ? 1 : INTENTOS;

  for (let intento = 1; ; intento++) {
    let idDocumento;
    let orden = null;

    if (campoId) {
      idDocumento = formData[campoId.key];
    } else {
      const resultado = asignacion
        ? await generarCodigoAsignacion()
        : await generarCodigoEvento(data.area, sectores);
      idDocumento = resultado.id;
      orden = resultado.orden;
    }

    const dataObj = {
      ...data,
      orden,
      fecha: new Date().toISOString(),
      usuario: user?.nombreCompleto || "",
    };

    try {
      if (asignacion) {
        await createAsignacion({ id: String(idDocumento), ...dataObj });
      } else {
        await addEvento({ id: String(idDocumento), ...dataObj });
      }
    } catch (error) {
      if (esNumeroRepetido(error) && intento < intentos) continue;
      throw error;
    }

    if (avisar) {
      Swal.fire(
        "Carga correcta",
        `Elemento ${idDocumento} creado correctamente.`,
        "success"
      );
    }
    return { id: String(idDocumento) };
  }
};

/* Lo que se le dice a la persona cuando un evento no se guardó. Nunca «No se
 * pudo consultar»: lo que falló es una escritura, y la ventana sigue abierta
 * con lo que cargó. */
export const textoDeEventoNoGuardado = (error) => {
  const causa = esNumeroRepetido(error)
    ? "el número del movimiento ya estaba usado y no se consiguió otro"
    : error?.message || "la base no contestó";
  return `El movimiento no se guardó (${causa}). Lo que cargaste sigue en ` +
    "la ventana: probá Guardar de nuevo y, si sigue, avisale a administración.";
};

const generarCodigoEvento = async (area, sectores) => {

  const areaString = String(area).trim().toLowerCase();

  const sector = sectores.find(
    (sec) => sec.nombre.toLowerCase() === areaString
  );

  if (!sector) {
    throw new Error(`Sector inválido: ${area}`);
  }

  const { data: nuevoOrden, error } = await supabase.rpc(
    "incrementar_contador",
    { tabla: "sectores", fila_id: sector.id },
  );

  if (error) throw error;

  const orden = enRango(nuevoOrden);
  if (!Number.isFinite(orden) || orden >= TOPE) {
    throw new Error(`El contador del sector ${sector.id} devolvió ${nuevoOrden}`);
  }

  return { id: idEvento(sector.id, orden), orden };
};

const generarCodigoAsignacion = async () => {

  const { data: nuevoOrden, error } = await supabase.rpc(
    "incrementar_contador",
    { tabla: "contadores", fila_id: "asignaciones" },
  );

  if (error) throw error;

  return {
    id: `AS${String(nuevoOrden).padStart(8, "0")}`,
    orden: nuevoOrden,
  };
};


/// funcion vieja de codigo de asignacion

/*
const generarCodigoAsignacion = async (area, sectores) => {

  const areaString = String(area).trim().toLowerCase();

  const sector = sectores.find(
    (sec) => sec.nombre.toLowerCase() === areaString
  );

  if (!sector) {
    throw new Error(`Sector inválido: ${area}`);
  }

  const nuevoOrden = Number(sector.ultimo || 0) + 1;

  await updateDoc(
    doc(db, "sectores", sector.id),
    { ultimo: nuevoOrden }
  );
  const codigoSector = String(sector.id).padStart(3, "0");

  return {
    id: `A${codigoSector}-${String(nuevoOrden).padStart(8, "0")}`,
    orden: nuevoOrden,
  };
};



*/